/**
 * GpsMonitoreo — consola en vivo (lista | mapa | detalle).
 *
 * MOCKUP: los datos vienen de `gpsMock.gpsApi` (en memoria); la simulación de
 * movimiento la maneja `useGpsLive`. La capa de mapa es Leaflet vanilla a
 * través del shim local (sin react-leaflet).
 *
 * Pendiente para integración real (ver gpsMock.gpsApi):
 *  - Reemplazar gpsApi.devices/geocercas/alarmas por fetch al MS-GPS-TRACKER.
 *  - Reemplazar `useGpsLive` por suscripción WS al stream del adapter.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, BatteryWarning, CircleDot, Layers, Plus, Radar, RefreshCw, Satellite,
} from 'lucide-react';
import useTheme from 'hooks/useTheme';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { cn } from '../../../utils/cn';
import { Button } from '../../../components/ui/Button';
import { PageHeader } from '../../../components/ui/PageHeader';
import { gpsApi } from './gpsMock';
import { useGpsLive } from './useGpsLive';
import { DeviceList } from './components/DeviceList';
import { DeviceDetailPanel } from './components/DeviceDetailPanel';
import { KpiStrip } from './components/KpiStrip';
import FleetMap from './components/FleetMap';

// Mapa estado → token de estado (paridad con KPI/Legend/marcadores). El color
// "duro" (hex) vive en gpsMock SOLO para la capa Leaflet (no theme-able).
const ESTADO_DOT = {
  movimiento: 'bg-status-success',
  detenido:   'bg-status-info',
  inactivo:   'bg-status-warning',
  sin_senal:  'bg-status-neutral',
};

export default function GpsMonitoreo() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const isMobile = useIsMobile(1024); // por debajo de lg: lista | mapa toggle
  const [mobileView, setMobileView] = useState('mapa');
  const [searchParams] = useSearchParams();

  const [devices, setDevices] = useState(null);
  const [geocercas, setGeocercas] = useState([]);
  const [alarmas, setAlarmas] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState(null);
  const [showGeocercas, setShowGeocercas] = useState(true);

  // Carga inicial (mock).
  useEffect(() => {
    let cancelled = false;
    // TODO(back): cablear cuando el back exponga /devices, /geocercas, /alarmas.
    Promise.all([gpsApi.devices(), gpsApi.geocercas(), gpsApi.alarmas()]).then(([d, g, a]) => {
      if (cancelled) return;
      setDevices(d);
      setGeocercas(g);
      setAlarmas(a);
    });
    return () => { cancelled = true; };
  }, []);

  // Deep-link `?device=ID` (desde el inventario o las alarmas).
  useEffect(() => {
    const id = searchParams.get('device');
    if (id && devices?.some((d) => d.id === id)) {
      setSelectedId(id);
      setFocusId(id);
    }
  }, [searchParams, devices]);

  const stableDevices = useMemo(() => devices ?? [], [devices]);
  const live = useGpsLive(stableDevices, 3000);

  const filtered = useMemo(() => {
    if (!devices) return [];
    let arr = devices;
    // `online` y `alarma` son filtros derivados (disparados desde la KpiStrip);
    // el resto coincide 1:1 con d.estado (paridad con los chips de DeviceList).
    if (estadoFilter === 'online') arr = arr.filter((d) => d.estado !== 'sin_senal');
    else if (estadoFilter === 'alarma') arr = arr.filter((d) => (d.alarmasActivas?.length ?? 0) > 0);
    else if (estadoFilter) arr = arr.filter((d) => d.estado === estadoFilter);
    if (search) {
      const s = search.toLowerCase();
      arr = arr.filter(
        (d) =>
          d.patente.toLowerCase().includes(s) ||
          d.alias.toLowerCase().includes(s) ||
          d.imei.includes(s) ||
          (d.conductorNombre ?? '').toLowerCase().includes(s),
      );
    }
    return arr;
  }, [devices, estadoFilter, search]);

  const selected = devices?.find((d) => d.id === selectedId) ?? null;
  const alarmasNoResueltas = alarmas.filter((a) => !a.resuelta);

  const counts = useMemo(() => {
    if (!devices) return { total: 0, online: 0, mov: 0, det: 0, inact: 0, off: 0 };
    return {
      total: devices.length,
      online: devices.filter((d) => d.estado !== 'sin_senal').length,
      mov:   devices.filter((d) => d.estado === 'movimiento').length,
      det:   devices.filter((d) => d.estado === 'detenido').length,
      inact: devices.filter((d) => d.estado === 'inactivo').length,
      off:   devices.filter((d) => d.estado === 'sin_senal').length,
    };
  }, [devices]);

  const handleSelect = (id) => {
    setSelectedId(id);
    setFocusId(id);
  };

  // `filter` mapea cada KPI al valor de estadoFilter que dispara al clicar.
  // `online`/`alarma` son derivados; el resto coincide 1:1 con d.estado.
  const kpiItems = [
    { label: t('Dispositivos'),    value: counts.total,  tone: 'neutral', filter: null,         icon: <Satellite      className="w-3.5 h-3.5" /> },
    { label: t('En línea'),        value: counts.online, tone: 'info',    filter: 'online',      icon: <Radar          className="w-3.5 h-3.5" /> },
    { label: t('En movimiento'),   value: counts.mov,    tone: 'success', filter: 'movimiento',  icon: <CircleDot      className="w-3.5 h-3.5" />, dot: ESTADO_DOT.movimiento },
    { label: t('Detenidos'),       value: counts.det,    tone: 'info',    filter: 'detenido',    icon: <CircleDot      className="w-3.5 h-3.5" />, dot: ESTADO_DOT.detenido },
    { label: t('Inactivos'),       value: counts.inact,  tone: 'warning', filter: 'inactivo',    icon: <CircleDot      className="w-3.5 h-3.5" />, dot: ESTADO_DOT.inactivo },
    { label: t('Sin señal'),       value: counts.off,    tone: 'neutral', filter: 'sin_senal',   icon: <BatteryWarning className="w-3.5 h-3.5" />, dot: ESTADO_DOT.sin_senal },
    { label: t('Alarmas activas'), value: alarmasNoResueltas.length, tone: alarmasNoResueltas.length > 0 ? 'error' : 'neutral', filter: 'alarma', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="w-full min-w-0 p-4 md:p-6 space-y-4">
      <PageHeader
        title={t("Administración de GPS")}
        subtitle={t("Flota propia · monitoreo en vivo, comandos, geocercas y alarmas.")}
        actions={
          <>
            <LiveBadge count={counts.mov} />
            <Button
              variant="outline"
              size="sm"
              iconLeft={<RefreshCw className="w-3.5 h-3.5" />}
              onClick={() => gpsApi.devices().then(setDevices)}
            >
              {t("Refrescar")}
            </Button>
            <Button size="sm" iconLeft={<Plus className="w-3.5 h-3.5" />}>{t("Vincular dispositivo")}</Button>
          </>
        }
      />

      <KpiStrip
        items={kpiItems}
        value={estadoFilter}
        onFilter={(f) => setEstadoFilter((prev) => (prev === f ? null : f))}
      />

      <div className="rounded-xl border border-border bg-bg overflow-hidden shadow-card">
        {/* En mobile/tablet: toggle Lista | Mapa para que ninguno se comprima. */}
        <div className="lg:hidden flex items-center gap-1 p-2 border-b border-border">
          {[
            { id: 'lista', label: t('Lista') },
            { id: 'mapa',  label: t('Mapa') },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setMobileView(opt.id)}
              aria-pressed={mobileView === opt.id}
              className={cn(
                'flex-1 h-9 text-sm font-medium rounded-lg transition-colors',
                mobileView === opt.id ? 'bg-brand-500 text-white' : 'bg-bg-muted text-fg-muted hover:text-fg',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="grid h-[calc(100svh-260px)] min-h-[480px] lg:min-h-[560px] grid-cols-1 lg:grid-cols-[340px_1fr]">
          <div className={cn('min-h-0', isMobile && mobileView !== 'lista' && 'hidden')}>
            <DeviceList
              devices={filtered}
              live={live}
              selectedId={selectedId}
              onSelect={(id) => {
                handleSelect(id);
                // En mobile la lista y el mapa son excluyentes: al elegir un
                // dispositivo se pasa al mapa para que el vuelo sea visible.
                if (isMobile) setMobileView('mapa');
              }}
              search={search}
              onSearch={setSearch}
              estadoFilter={estadoFilter}
              onEstadoFilter={setEstadoFilter}
              loading={devices === null}
            />
          </div>

          {/* Mapa + detalle acoplado a la derecha */}
          <div className={cn('relative min-h-0', isMobile && mobileView !== 'mapa' && 'hidden')}>
            <FleetMap
              className="absolute inset-0"
              devices={devices ?? []}
              geocercas={geocercas}
              live={live}
              selectedId={selectedId}
              focusId={focusId}
              showGeocercas={showGeocercas}
              isDark={isDark}
              onSelect={handleSelect}
            />

            {/* Controles flotantes */}
            <div className="absolute top-3 left-3 z-[400] flex flex-col gap-2">
              <button
                onClick={() => setShowGeocercas((v) => !v)}
                className={cn(
                  'inline-flex items-center gap-2 h-9 px-3 text-sm font-medium rounded-lg border border-border shadow-elevated',
                  showGeocercas ? 'bg-bg text-fg' : 'bg-bg text-fg-muted',
                )}
                title={t("Mostrar/ocultar geocercas")}
              >
                <Layers className="w-4 h-4" />
                {t("Geocercas")}
                <span className="tabular-nums text-[10px] bg-bg-muted px-1.5 rounded">{geocercas.length}</span>
              </button>
            </div>

            <Legend className="absolute bottom-3 left-3 z-[400]" />

            {selected && (
              <DeviceDetailPanel
                device={selected}
                live={live[selected.id]}
                alarmas={alarmas}
                onClose={() => setSelectedId(null)}
                className="absolute top-0 right-0 bottom-0 w-full sm:w-[420px] z-[500]"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Atoms ───────────────────────────────────────────────────────────────────

function LiveBadge({ count }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-status-success-soft text-status-success text-xs font-medium">
      <span className="relative flex w-1.5 h-1.5">
        <span className="absolute inset-0 rounded-full bg-status-success opacity-60 motion-safe:animate-ping" />
        <span className="relative w-1.5 h-1.5 rounded-full bg-status-success" />
      </span>
      <span className="tabular-nums">{count}</span> {t("en vivo")}
    </span>
  );
}

function Legend({ className }) {
  const { t } = useTranslation();
  const items = [
    { dot: ESTADO_DOT.movimiento, label: t('Movimiento') },
    { dot: ESTADO_DOT.detenido,   label: t('Detenido') },
    { dot: ESTADO_DOT.inactivo,   label: t('Inactivo') },
    { dot: ESTADO_DOT.sin_senal,  label: t('Sin señal') },
  ];
  return (
    <div className={cn('bg-bg border border-border rounded-lg shadow-elevated px-3 py-2', className)}>
      <div className="text-[10px] uppercase tracking-wide font-semibold text-fg-muted mb-1.5">{t("Estado")}</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {items.map((it) => (
          <span key={it.label} className="inline-flex items-center gap-1.5 text-xs text-fg">
            <span className={cn('w-2 h-2 rounded-full', it.dot)} aria-hidden />
            {it.label}
          </span>
        ))}
      </div>
    </div>
  );
}
